import { ReactNode, useEffect, useRef, useState } from 'react';
import { Box, Paper } from '@mui/material';
import { DragIndicator } from '@mui/icons-material';

interface IProps {
  children: ReactNode;
  /** Startbreite des schwebenden Panels (px). */
  width?: number;
  /** Starthöhe des schwebenden Panels (px). */
  height?: number;
}

const MIN_WIDTH = 360;
const MIN_HEIGHT = 320;

/**
 * Schwebendes, verschieb- UND größenveränderbares Overlay-Panel (#1181).
 *
 * Wird für die „Bild-in-Bild"-Ansicht von Dokumenten/Rechnungen verwendet:
 * Statt das Formular in eine schmale Spalte zu quetschen, legt sich die
 * Vorschau als bewegliches Fenster über den Dialog. Verschoben wird an der
 * Griffleiste oben, in der Größe gezogen am Griff unten rechts. Position ist
 * `fixed`, damit das Panel beim Scrollen des Formulars nicht wegläuft.
 */
export const DraggableSidepanel = ({
  children,
  width = 600,
  height = 700,
}: IProps) => {
  const [pos, setPos] = useState({ top: 90, left: 24 });
  const [size, setSize] = useState({ width, height });
  const [mode, setMode] = useState<'idle' | 'drag' | 'resize'>('idle');
  const start = useRef({
    mouseX: 0,
    mouseY: 0,
    top: 0,
    left: 0,
    width: 0,
    height: 0,
  });

  const begin = (nextMode: 'drag' | 'resize') => (event: React.MouseEvent) => {
    start.current = {
      mouseX: event.clientX,
      mouseY: event.clientY,
      top: pos.top,
      left: pos.left,
      width: size.width,
      height: size.height,
    };
    setMode(nextMode);
    event.preventDefault();
    event.stopPropagation();
  };

  useEffect(() => {
    if (mode === 'idle') {
      return;
    }
    const onMove = (event: MouseEvent) => {
      const dx = event.clientX - start.current.mouseX;
      const dy = event.clientY - start.current.mouseY;
      if (mode === 'drag') {
        setPos({
          top: Math.min(
            Math.max(0, start.current.top + dy),
            window.innerHeight - 80
          ),
          left: Math.min(
            Math.max(0, start.current.left + dx),
            window.innerWidth - 200
          ),
        });
      } else {
        setSize({
          width: Math.max(
            MIN_WIDTH,
            Math.min(start.current.width + dx, window.innerWidth * 0.98)
          ),
          height: Math.max(
            MIN_HEIGHT,
            Math.min(start.current.height + dy, window.innerHeight * 0.95)
          ),
        });
      }
    };
    const onUp = () => setMode('idle');
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [mode]);

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        width: size.width,
        height: size.height,
        maxWidth: '98vw',
        maxHeight: '95vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: 2,
        zIndex: theme => theme.zIndex.modal + 1,
      }}
    >
      <Box
        onMouseDown={begin('drag')}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 0.25,
          flexShrink: 0,
          cursor: mode === 'drag' ? 'grabbing' : 'grab',
          bgcolor: 'action.hover',
          borderBottom: 1,
          borderColor: 'divider',
          userSelect: 'none',
        }}
      >
        <DragIndicator fontSize='small' color='disabled' />
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>{children}</Box>
      {/* Griff zum Vergrößern/Verkleinern (unten rechts). */}
      <Box
        onMouseDown={begin('resize')}
        sx={{
          position: 'absolute',
          right: 3,
          bottom: 3,
          width: 12,
          height: 12,
          cursor: 'nwse-resize',
          borderRight: '2px solid',
          borderBottom: '2px solid',
          borderColor: 'text.secondary',
          opacity: 0.5,
        }}
      />
    </Paper>
  );
};
